import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "@iconify/react";
import { IMAGES } from "@/assets/images";
import { useLang } from "@/i18n";

const caseStudiesMeta = [
  {
    brand: "Alipay",
    logo: "/images/logo_alipay.png",
    logoBg: "#ffffff",
    icon: "mdi:wallet",
    color: "#1677FF",
    platforms: ["YouTube", "Instagram", "TikTok"],
  },
  {
    brand: "HONGQI",
    logo: "/images/logo_hongqi.png",
    logoBg: "#ffffff",
    icon: "mdi:car-electric",
    color: "#ef4444",
    platforms: ["Instagram", "TikTok", "YouTube"],
  },
  {
    brand: "TEMU",
    logo: "/images/logo_temu.png",
    logoBg: "#FF6600",
    icon: "mdi:shopping",
    color: "#f4440c",
    platforms: ["TikTok", "Instagram", "YouTube"],
  },
  {
    brand: "Ulike",
    logo: "/images/logo_ulike.png",
    logoBg: "#E91E8C",
    icon: "mdi:flash",
    color: "#ec4899",
    platforms: ["TikTok", "Instagram", "YouTube"],
  },
  {
    brand: "Hisense",
    logo: "/images/logo_hisense.png",
    logoBg: "#ffffff",
    icon: "mdi:television",
    color: "#0ea5e9",
    platforms: ["Instagram", "YouTube"],
  },
  {
    brand: "Macao Tourism Board",
    logo: "/images/logo_macau.png",
    logoBg: "#00875A",
    icon: "mdi:map-marker",
    color: "#f97316",
    platforms: ["TikTok", "Instagram", "YouTube"],
  },
  {
    brand: "SENSER",
    logo: "/images/logo_senser.png",
    logoBg: "#ffffff",
    icon: "mdi:diamond-stone",
    color: "#8b5cf6",
    platforms: ["Instagram", "YouTube"],
  },
  {
    brand: "LYNK & CO",
    logo: "/images/logo_lynkco.png",
    logoBg: "#ffffff",
    icon: "mdi:car-sports",
    color: "#f4440c",
    platforms: ["TikTok"],
  },
  {
    brand: "Jagat",
    logo: "/images/logo_jagat.png",
    logoBg: "#6C3CE1",
    icon: "mdi:earth",
    color: "#14b8a6",
    platforms: ["TikTok"],
  },
  {
    brand: "imo",
    logo: "/images/logo_imo.png",
    logoBg: "#f0f8ff",
    icon: "mdi:message-video",
    color: "#22c55e",
    platforms: ["TikTok", "Instagram"],
  },
  {
    brand: "POIZON",
    logo: "/images/logo_poizon.png",
    logoBg: "#ffffff",
    icon: "mdi:shoe-sneaker",
    color: "#f4440c",
    platforms: ["YouTube", "TikTok", "Instagram"],
  },
  {
    brand: "Chunlai",
    logo: "/images/logo_chunlai.png",
    logoBg: "#ffffff",
    icon: "mdi:cup-water",
    color: "#06b6d4",
    platforms: ["TikTok", "Instagram"],
  },
  {
    brand: "Dahz Ice Cacao",
    logo: "/images/logo_dahz.png",
    logoBg: "#ffffff",
    icon: "mdi:coffee",
    color: "#a855f7",
    platforms: ["TikTok", "Instagram"],
  },
  {
    brand: "ELFBAR",
    logo: "/images/logo_elfbar.png",
    logoBg: "#f5f5ff",
    icon: "mdi:smoke",
    color: "#6366f1",
    platforms: ["TikTok", "Instagram", "YouTube"],
  },
  {
    brand: "Geekvape",
    logo: "/images/logo_geekvape.png",
    logoBg: "#FF4500",
    icon: "mdi:lightning-bolt",
    color: "#f59e0b",
    platforms: ["TikTok", "Instagram"],
  },
  {
    brand: "Vaporesso",
    logo: "/images/logo_vaporesso.png",
    logoBg: "#f0f4ff",
    icon: "mdi:weather-windy",
    color: "#3b82f6",
    platforms: ["YouTube", "Instagram"],
  },
  {
    brand: "DOJO",
    logo: "/images/logo_dojo.png",
    logoBg: "#f5f5f5",
    icon: "mdi:creation",
    color: "#10b981",
    platforms: ["TikTok", "Instagram"],
  },
  {
    brand: "Lost Vape",
    logo: "/images/logo_lostvape.png",
    logoBg: "#faf5ff",
    icon: "mdi:smoke",
    color: "#9800C5",
    platforms: ["Instagram", "YouTube"],
  },
];

const avatarImages = [IMAGES.AVATAR_WOMAN_3, IMAGES.AVATAR_MAN_1, IMAGES.AVATAR_MAN_4];

const testimonials_unused = [
  {
    quote: "Ascend对海外市场的深刻理解让我们印象深刻。他们不只是执行团队，更是真正懂品牌的战略伙伴。从策划到复盘报告，每个环节都展现出极高的专业水准。",
    author: "Sarah Mitchell",
    title: "全球品牌总监",
    company: "头部跨境电商品牌",
    market: "美国 · 欧洲",
    avatarImg: IMAGES.AVATAR_WOMAN_3,
  },
  {
    quote: "KOL筛选流程严格透明，每一位达人都精准契合我们的品牌调性。与Ascend合作后，中东市场声量爆发式增长——5天内突破2000万播放量，完全超出预期。",
    author: "James Whitfield",
    title: "市场营销总监",
    company: "高端新能源汽车品牌",
    market: "中东 · 沙特阿拉伯",
    avatarImg: IMAGES.AVATAR_MAN_1,
  },
  {
    quote: "Ascend帮助我们在东南亚快速规模化增长。他们对本地达人生态的深入了解和内容本土化能力，让我们在竞争中占据明显优势，ROI远超行业基准。",
    author: "Lucas Bennett",
    title: "增长负责人",
    company: "全球科技与社交平台",
    market: "东南亚 · 日本",
    avatarImg: IMAGES.AVATAR_MAN_4,
  },
];

const platformColors: Record<string, string> = {
  "TikTok": "#010101",
  "Instagram": "#E1306C",
  "YouTube": "#FF0000",
  "Facebook": "#1877F2",
  "RedNote": "#FF2442",
};

const ease = [0.4, 0, 0.2, 1];
const PAGE_SIZE = 6;

export default function Testimonials() {
  const { t } = useLang();
  const caseStudies = t.testimonials.caseStudies.map((cs, i) => ({
    ...caseStudiesMeta[i] ?? {},
    ...cs,
  }));
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(caseStudies.length / PAGE_SIZE);
  const visible = caseStudies.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const goNext = () => setPage(p => Math.min(p + 1, totalPages - 1));
  const goPrev = () => setPage(p => Math.max(p - 1, 0));

  return (
    <section id="testimonials" style={{ background: "#0a0a0a", overflow: "hidden" }}>
      {/* ── Case Studies ── */}
      <div className="max-w-7xl mx-auto section-pad">

        {/* Header row */}
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "2rem", marginBottom: "4rem", flexWrap: "wrap" }}>
          <div>
            <motion.div
              initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.8, ease }}
              style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.2rem" }}
            >
              <div style={{ width: 32, height: 1.5, background: "#f4440c" }} />
              <span style={{ fontSize: "0.78rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#f4440c", fontWeight: 500 }}>
                {t.testimonials.eyebrow}
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.1, ease }}
              style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "clamp(2.4rem, 4.5vw, 3.6rem)", fontWeight: 700, color: "#fff", lineHeight: 1.0, letterSpacing: "-0.04em", margin: 0 }}
            >
              {t.testimonials.headline1}<br />
              <span style={{ color: "transparent", WebkitTextStroke: "1.5px #f4440c", fontFamily: "'Inter Tight', sans-serif", fontWeight: 700 }}>{t.testimonials.headline2}</span>
            </motion.h2>
          </div>

          {/* Pagination Controls (top right) */}
          <motion.div
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
            viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }}
            style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.75rem" }}
          >
            <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.4)", letterSpacing: "0.06em" }}>
              <span style={{ color: "#fff", fontWeight: 600 }}>{page + 1}</span>
              <span style={{ margin: "0 0.4rem" }}>/</span>
              <span>{totalPages}</span>
              <span style={{ marginLeft: "0.5rem" }}> / {caseStudies.length}</span>
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button
                onClick={goPrev}
                disabled={page === 0}
                style={{
                  width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center",
                  background: page === 0 ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.12)",
                  border: "1px solid",
                  borderColor: page === 0 ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.2)",
                  cursor: page === 0 ? "not-allowed" : "pointer",
                  transition: "all 0.25s",
                }}
                onMouseEnter={e => { if (page > 0) { (e.currentTarget as HTMLElement).style.background = "#f4440c"; (e.currentTarget as HTMLElement).style.borderColor = "#f4440c"; } }}
                onMouseLeave={e => { if (page > 0) { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.12)"; (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.2)"; } }}
              >
                <Icon icon="mdi:chevron-left" style={{ fontSize: 18, color: page === 0 ? "rgba(255,255,255,0.2)" : "#fff" }} />
              </button>
              <button
                onClick={goNext}
                disabled={page === totalPages - 1}
                style={{
                  width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center",
                  background: page === totalPages - 1 ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.12)",
                  border: "1px solid",
                  borderColor: page === totalPages - 1 ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.2)",
                  cursor: page === totalPages - 1 ? "not-allowed" : "pointer",
                  transition: "all 0.25s",
                }}
                onMouseEnter={e => { if (page < totalPages - 1) { (e.currentTarget as HTMLElement).style.background = "#f4440c"; (e.currentTarget as HTMLElement).style.borderColor = "#f4440c"; } }}
                onMouseLeave={e => { if (page < totalPages - 1) { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.12)"; (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.2)"; } }}
              >
                <Icon icon="mdi:chevron-right" style={{ fontSize: 18, color: page === totalPages - 1 ? "rgba(255,255,255,0.2)" : "#fff" }} />
              </button>
            </div>
          </motion.div>
        </div>

        {/* Case Cards Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={page}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4, ease }}
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))", gap: "1.5rem" }}
          >
            {visible.map((cs, i) => (
              <CaseCard key={cs.brand} cs={cs} i={i} />
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Bottom pagination dots + hint */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "1.5rem", marginTop: "3rem" }}>
          {/* Prev hint */}
          <button
            onClick={goPrev}
            disabled={page === 0}
            style={{ display: "flex", alignItems: "center", gap: "0.4rem", background: "none", border: "none", cursor: page === 0 ? "not-allowed" : "pointer", opacity: page === 0 ? 0.25 : 1, transition: "opacity 0.25s" }}
          >
            <Icon icon="mdi:arrow-left" style={{ fontSize: 14, color: "rgba(255,255,255,0.35)" }} />
            <span style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.35)", letterSpacing: "0.06em" }}>{t.testimonials.prevPage}</span>
          </button>

          {/* Dot indicators */}
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            {Array.from({ length: totalPages }).map((_, pi) => (
              <button
                key={pi}
                onClick={() => setPage(pi)}
                style={{
                  width: pi === page ? 24 : 7, height: 7,
                  background: pi === page ? "#f4440c" : "rgba(255,255,255,0.15)",
                  border: "none", cursor: "pointer",
                  transition: "all 0.3s ease",
                  borderRadius: 4, padding: 0,
                }}
              />
            ))}
          </div>

          {/* Next hint */}
          <button
            onClick={goNext}
            disabled={page === totalPages - 1}
            style={{ display: "flex", alignItems: "center", gap: "0.4rem", background: "none", border: "none", cursor: page === totalPages - 1 ? "not-allowed" : "pointer", opacity: page === totalPages - 1 ? 0.25 : 1, transition: "opacity 0.25s" }}
          >
            <span style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.35)", letterSpacing: "0.06em" }}>{t.testimonials.nextPage}</span>
            <Icon icon="mdi:arrow-right" style={{ fontSize: 14, color: "rgba(255,255,255,0.35)" }} />
          </button>
        </div>
      </div>

      {/* ── Testimonial Quotes ── */}
      <div style={{ background: "#0a0a0a" }}>
        <div className="max-w-7xl mx-auto section-pad">
          <motion.div
            initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8, ease }}
            style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "3.5rem" }}
          >
            <div style={{ width: 32, height: 1.5, background: "#f4440c" }} />
            <span style={{ fontSize: "0.78rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#f4440c", fontWeight: 400 }}>
              {t.testimonials.reviewsTitle}
            </span>
          </motion.div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))", gap: "1.5rem" }}>
            {t.testimonials.reviews.map((rev, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1, ease }}
                style={{
                  padding: "2.5rem",
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  position: "relative",
                  transition: "border-color 0.3s",
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = "rgba(244,68,12,0.3)"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.07)"; }}
              >
                {/* Quote icon */}
                <Icon icon="mdi:format-quote-open" style={{ fontSize: 28, color: "#f4440c", opacity: 0.5, marginBottom: "1.25rem", display: "block" }} />

                {/* Quote text */}
                <p style={{ fontSize: "0.93rem", color: "rgba(255,255,255,0.7)", lineHeight: 1.85, marginBottom: "2rem", fontWeight: 300, fontStyle: "italic" }}>
                  "{rev.quote}"
                </p>

                {/* Divider */}
                <div style={{ height: 1, background: "rgba(255,255,255,0.07)", marginBottom: "1.5rem" }} />

                {/* Author with avatar */}
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                  {/* Avatar */}
                  <div style={{
                    width: 52, height: 52, borderRadius: "50%",
                    overflow: "hidden",
                    flexShrink: 0,
                    border: "2px solid rgba(244,68,12,0.35)",
                  }}>
                    <img
                      src={avatarImages[i]}
                      alt={rev.author}
                      style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }}
                    />
                  </div>
                  {/* Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "0.88rem", color: "#fff", fontWeight: 600 }}>{rev.author}</div>
                    <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.35)", marginTop: "0.15rem" }}>{rev.title} · {rev.company}</div>
                  </div>
                  {/* Market tag */}
                  <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                    <Icon icon="mdi:map-marker-outline" style={{ fontSize: 11, color: "#f4440c" }} />
                    <span style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.25)", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>{rev.market}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

type CaseData = {
  brand: string;
  logo?: string;
  logoBg?: string;
  category: string;
  market: string;
  icon?: string;
  color?: string;
  headline: string;
  detail: string;
  metrics: { value: string; unit: string; label: string }[];
  platforms?: string[];
};

function CaseCard({ cs, i }: { cs: CaseData; i: number }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.55, delay: Math.min(i * 0.07, 0.28), ease: [0.4, 0, 0.2, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? "#181818" : "#141414",
        padding: "0",
        transition: "all 0.3s ease",
        cursor: "default",
        boxShadow: hovered ? "0 16px 48px rgba(0,0,0,0.5)" : "none",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        borderRadius: 0,
        overflow: "hidden",
        border: `1px solid ${hovered ? "rgba(244,68,12,0.25)" : "rgba(255,255,255,0.06)"}`,
      }}
    >
      {/* Top accent bar */}
      <div style={{ height: 2, background: hovered ? cs.color : "rgba(255,255,255,0.06)", transition: "background 0.3s" }} />

      <div style={{ padding: "1.75rem" }}>
        {/* Header: Logo + Brand + Category */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
            {/* Logo */}
            <div style={{
              width: 44, height: 44,
              borderRadius: 8,
              overflow: "hidden",
              background: cs.logoBg,
              flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <img
                src={cs.logo}
                alt={cs.brand}
                style={{ width: "90%", height: "90%", objectFit: "contain" }}
                onError={e => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                  const parent = (e.currentTarget as HTMLImageElement).parentElement;
                  if (parent) {
                    parent.innerHTML = `<span style="font-size:0.55rem;font-weight:700;color:#fff;letter-spacing:0.02em;text-align:center;padding:2px">${cs.brand.substring(0,4)}</span>`;
                  }
                }}
              />
            </div>
            <div>
              <div style={{ fontSize: "1rem", fontWeight: 700, color: "#fff", lineHeight: 1.15, letterSpacing: "-0.01em" }}>
                {cs.brand}
              </div>
              <div style={{ fontSize: "0.68rem", color: cs.color, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 500, marginTop: "0.15rem" }}>
                {cs.category}
              </div>
            </div>
          </div>
          {/* Market */}
          <div style={{
            fontSize: "0.65rem", color: "rgba(255,255,255,0.35)", textAlign: "right",
            maxWidth: 100, lineHeight: 1.4,
            letterSpacing: "0.02em",
          }}>
            {cs.market}
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "rgba(255,255,255,0.06)", marginBottom: "1.1rem" }} />

        {/* Headline */}
        <h3 style={{ fontSize: "0.9rem", fontWeight: 600, color: "rgba(255,255,255,0.9)", lineHeight: 1.5, marginBottom: "0.75rem", minHeight: "2.7em" }}>
          {cs.headline}
        </h3>

        {/* Detail */}
        <p style={{ fontSize: "0.79rem", color: "rgba(255,255,255,0.4)", lineHeight: 1.7, marginBottom: "1.25rem" }}>
          {cs.detail.length > 90 ? cs.detail.substring(0, 88) + "…" : cs.detail}
        </p>

        {/* Metrics */}
        <div style={{ display: "flex", gap: "1.25rem", marginBottom: "1.25rem", paddingTop: "1rem", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
          {cs.metrics.map((m, mi) => (
            <div key={mi}>
              <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "1.4rem", fontWeight: 600, color: cs.color, lineHeight: 1, letterSpacing: "-0.01em" }}>
                {m.value}
                {m.unit && <span style={{ fontSize: "0.72rem", marginLeft: "0.15rem", fontFamily: "inherit" }}>{m.unit}</span>}
              </div>
              <div style={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.3)", letterSpacing: "0.06em", textTransform: "uppercase", marginTop: "0.2rem" }}>
                {m.label}
              </div>
            </div>
          ))}
        </div>

        {/* Platform tags */}
        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
          {cs.platforms.map(p => (
            <span
              key={p}
              style={{
                fontSize: "0.6rem", padding: "0.25rem 0.6rem",
                background: `${platformColors[p] || "rgba(255,255,255,0.1)"}22`,
                color: platformColors[p] || "rgba(255,255,255,0.6)",
                borderRadius: 2, fontWeight: 600, letterSpacing: "0.04em",
                border: `1px solid ${platformColors[p] || "rgba(255,255,255,0.15)"}35`,
              }}
            >
              {p}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}